import { useState } from 'react';
import { CodeField, codePayload, type CodeValue } from '@/components/CodeField';
import { Link } from 'react-router-dom';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { LoadingState } from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { StatusBadge } from '@/components/StatusBadge';
import { RequirePermission, PermissionDenied } from '@/components/RequirePermission';
import { useAuth } from '@/auth/AuthContext';
import { useLanguage, useT } from '@/i18n/LanguageContext';
import { formatDateTime } from '@/i18n/format';
import type { TranslationKey } from '@/i18n/dictionaries/en';
import type { OnboardingStep } from '@/api/types';

/**
 * ADR-076 Parts A + D - guided tenant onboarding + the readiness/activation
 * screen the epic asks for as one surface (D depends entirely on A's state,
 * and splitting them into two pages would just be two fetches of the same
 * read). EVERY fact below comes straight from GET /api/v1/office/onboarding
 * - there is no hardcoded checklist here, only a label/description lookup
 * for each step key the backend already computed as done/blocked.
 *
 * This is an ORCHESTRATION layer: the actual writes (create legal entity,
 * warehouse, seller, contract, tariff, users) are ordinary domain
 * mutations - this page just calls them in the order onboarding needs and
 * re-reads readiness afterwards. No business rule about "is this tenant
 * ready" lives in this file; that is 100% server-computed.
 */

const STEP_META: Record<string, { titleKey: TranslationKey; descriptionKey: TranslationKey; helpTo?: string }> = {
  ORG_PROFILE: { titleKey: 'onboarding.stepOrgProfileTitle', descriptionKey: 'onboarding.stepOrgProfileDesc' },
  LEGAL_ENTITY: { titleKey: 'onboarding.stepLegalEntityTitle', descriptionKey: 'onboarding.stepLegalEntityDesc' },
  WAREHOUSE: { titleKey: 'onboarding.stepWarehouseTitle', descriptionKey: 'onboarding.stepWarehouseDesc' },
  WAREHOUSE_TIMEZONE: { titleKey: 'onboarding.stepWarehouseTimezoneTitle', descriptionKey: 'onboarding.stepWarehouseTimezoneDesc' },
  SYSTEM_LOCATIONS: { titleKey: 'onboarding.stepSystemLocationsTitle', descriptionKey: 'onboarding.stepSystemLocationsDesc' },
  FIRST_SELLER: { titleKey: 'onboarding.stepFirstSellerTitle', descriptionKey: 'onboarding.stepFirstSellerDesc' },
  SELLER_CONTRACT: { titleKey: 'onboarding.stepSellerContractTitle', descriptionKey: 'onboarding.stepSellerContractDesc', helpTo: '/billing' },
  TARIFF_ASSIGNMENT: { titleKey: 'onboarding.stepTariffAssignmentTitle', descriptionKey: 'onboarding.stepTariffAssignmentDesc', helpTo: '/billing' },
  INTERNAL_USERS: { titleKey: 'onboarding.stepInternalUsersTitle', descriptionKey: 'onboarding.stepInternalUsersDesc', helpTo: '/users' },
  SELLER_PORTAL_USERS: { titleKey: 'onboarding.stepSellerPortalUsersTitle', descriptionKey: 'onboarding.stepSellerPortalUsersDesc' },
  INITIAL_IMPORT: { titleKey: 'onboarding.stepInitialImportTitle', descriptionKey: 'onboarding.stepInitialImportDesc', helpTo: '/imports' },
  RECONCILIATION: { titleKey: 'onboarding.stepReconciliationTitle', descriptionKey: 'onboarding.stepReconciliationDesc', helpTo: '/imports' },
};

function StepRow({ step }: { step: OnboardingStep }): JSX.Element {
  const t = useT();
  const meta = STEP_META[step.step];
  const title = meta ? t(meta.titleKey) : step.step;
  const description = meta ? t(meta.descriptionKey) : '';
  return (
    <tr>
      <td>
        <div style={{ fontWeight: 600 }}>{title}</div>
        <div className="sub">{description}</div>
      </td>
      <td>
        {step.done ? (
          <StatusBadge value="ACTIVE" />
        ) : step.required ? (
          <span className="badge danger">{t('status.blocked')}</span>
        ) : (
          <span className="badge neutral">{t('onboarding.notRequired')}</span>
        )}
      </td>
      <td>{meta?.helpTo && !step.done ? <Link to={meta.helpTo}>{t('onboarding.goFixThis')}</Link> : null}</td>
    </tr>
  );
}

/**
 * Minimal inline forms for the org-structure steps that had no other screen
 * in this product at all (ADR-076 Part A). PARTNER DEMO POLISH: the legal
 * entity / warehouse pickers are real `<select>`s sourced from
 * endpoints.legalEntities()/warehousesList() (packages/operations/src/
 * services/orgSetup.ts's listLegalEntitiesSummary/listWarehousesSummary) -
 * an operator never needs to already know a raw id. A just-created row is
 * merged into the option list immediately so step 2 can select step 1's
 * result before the list re-fetches.
 */
function SetupForms({ onChanged }: { onChanged: () => void }): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const legalEntities = useApi(() => endpoints.legalEntities(), []);
  const warehouses = useApi(() => endpoints.warehousesList(), []);
  const [justCreatedLegalEntity, setJustCreatedLegalEntity] = useState<{ id: string; name: string } | null>(null);
  const [justCreatedWarehouse, setJustCreatedWarehouse] = useState<{ id: string; name: string } | null>(null);
  const [legalEntityId, setLegalEntityId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const t = useT();

  const legalEntityOptions = [
    ...(legalEntities.status === 'success' ? legalEntities.data.legalEntities : []),
    ...(justCreatedLegalEntity && (legalEntities.status !== 'success' || !legalEntities.data.legalEntities.some((e) => e.id === justCreatedLegalEntity.id))
      ? [{ id: justCreatedLegalEntity.id, name: justCreatedLegalEntity.name, country: '' }]
      : []),
  ];
  const warehouseOptions = [
    ...(warehouses.status === 'success' ? warehouses.data.warehouses : []),
    ...(justCreatedWarehouse && (warehouses.status !== 'success' || !warehouses.data.warehouses.some((w) => w.id === justCreatedWarehouse.id))
      ? [{ id: justCreatedWarehouse.id, name: justCreatedWarehouse.name, code: '', legal_entity_id: '' }]
      : []),
  ];

  const [whName, setWhName] = useState('');
  const [whCode, setWhCode] = useState<CodeValue>({ code: '', auto: true });
  const [sellerName, setSellerName] = useState('');
  const [sellerCode, setSellerCode] = useState<CodeValue>({ code: '', auto: true });

  async function run(fn: () => Promise<void>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h3 style={{ fontSize: 14, marginTop: 0 }}>{t('onboarding.setupOrgStructure')}</h3>
      {Boolean(error) && <ErrorBanner error={error} onRetry={() => setError(null)} />}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          const form = new FormData(e.currentTarget);
          const name = String(form.get('name'));
          void run(async () => {
            const { legalEntityId: id } = await endpoints.createLegalEntity({
              name,
              country: String(form.get('country')),
              currency: String(form.get('currency')),
            });
            setLegalEntityId(id);
            setJustCreatedLegalEntity({ id, name });
            legalEntities.reload();
          });
        }}
      >
        <h4 style={{ fontSize: 13, marginBottom: 8 }}>{t('onboarding.step1LegalEntity')}</h4>
        <div className="field-row">
          <label htmlFor="le-name">{t('onboarding.name')}</label>
          <input id="le-name" name="name" required disabled={busy} />
        </div>
        <div className="field-row">
          <label htmlFor="le-country">{t('onboarding.countryIso2')}</label>
          <input id="le-country" name="country" required maxLength={2} defaultValue="RS" disabled={busy} />
        </div>
        <div className="field-row">
          <label htmlFor="le-currency">{t('onboarding.currencyIso3')}</label>
          <input id="le-currency" name="currency" required maxLength={3} defaultValue="EUR" disabled={busy} />
        </div>
        <div className="form-actions">
          <button type="submit" disabled={busy}>
            {t('onboarding.createLegalEntity')}
          </button>
        </div>
      </form>

      <form
        style={{ marginTop: 16 }}
        onSubmit={(e) => {
          e.preventDefault();
          const form = new FormData(e.currentTarget);
          const name = String(form.get('name'));
          void run(async () => {
            const { warehouseId: id } = await endpoints.createWarehouse({
              legalEntityId: String(form.get('legalEntityId')),
              ...codePayload(whCode),
              name,
              timeZone: String(form.get('timeZone')),
            });
            setWhCode({ code: '', auto: true });
            setWhName('');
            setWarehouseId(id);
            setJustCreatedWarehouse({ id, name });
            warehouses.reload();
          });
        }}
      >
        <h4 style={{ fontSize: 13, marginBottom: 8 }}>{t('onboarding.step2Warehouse')}</h4>
        <div className="field-row">
          <label htmlFor="wh-legal-entity">{t('billing.legalEntity')}</label>
          <select id="wh-legal-entity" name="legalEntityId" required disabled={busy} value={legalEntityId} onChange={(e) => setLegalEntityId(e.target.value)}>
            <option value="" disabled>
              {t('billing.selectLegalEntity')}
            </option>
            {legalEntityOptions.map((le) => (
              <option key={le.id} value={le.id}>
                {le.name}
                {le.country ? ` (${le.country})` : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="field-row">
          <label htmlFor="wh-name">{t('onboarding.name')}</label>
          <input id="wh-name" name="name" required disabled={busy} value={whName} onChange={(e) => setWhName(e.target.value)} />
        </div>
        <CodeField entity="WAREHOUSE" id="wh-code" name={whName} value={whCode} onChange={setWhCode} disabled={busy} />
        <div className="field-row">
          <label htmlFor="wh-tz">{t('onboarding.timezoneIana')}</label>
          <input id="wh-tz" name="timeZone" required defaultValue="Europe/Belgrade" disabled={busy} />
        </div>
        <div className="form-actions">
          <button type="submit" disabled={busy || !legalEntityOptions.length}>
            {t('onboarding.createWarehouse')}
          </button>
        </div>
      </form>

      <form
        style={{ marginTop: 16 }}
        onSubmit={(e) => {
          e.preventDefault();
          const form = new FormData(e.currentTarget);
          void run(async () => {
            await endpoints.createSystemLocations(String(form.get('warehouseId')));
          });
        }}
      >
        <h4 style={{ fontSize: 13, marginBottom: 8 }}>{t('onboarding.step3SystemLocations')}</h4>
        <div className="field-row">
          <label htmlFor="loc-warehouse">{t('inbounds.colWarehouse')}</label>
          <select id="loc-warehouse" name="warehouseId" required disabled={busy} value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
            <option value="" disabled>
              {t('onboarding.selectWarehouse')}
            </option>
            {warehouseOptions.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
                {w.code ? ` (${w.code})` : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="form-actions">
          <button type="submit" disabled={busy || !warehouseOptions.length}>
            {t('onboarding.createSystemLocations')}
          </button>
        </div>
      </form>

      <form
        style={{ marginTop: 16 }}
        onSubmit={(e) => {
          e.preventDefault();
          const form = new FormData(e.currentTarget);
          void run(async () => {
            await endpoints.createSellerOwner({ ...codePayload(sellerCode), name: String(form.get('name')) });
            setSellerCode({ code: '', auto: true });
            setSellerName('');
          });
        }}
      >
        <h4 style={{ fontSize: 13, marginBottom: 8 }}>{t('onboarding.step4FirstSeller')}</h4>
        <div className="field-row">
          <label htmlFor="seller-name">{t('onboarding.name')}</label>
          <input id="seller-name" name="name" required disabled={busy} value={sellerName} onChange={(e) => setSellerName(e.target.value)} />
        </div>
        <CodeField entity="SELLER" id="seller-code" name={sellerName} value={sellerCode} onChange={setSellerCode} disabled={busy} />
        <div className="form-actions">
          <button type="submit" disabled={busy}>
            {t('onboarding.createSeller')}
          </button>
        </div>
      </form>

      <p className="sub" style={{ marginTop: 16 }}>
        {t('onboarding.otherStepsHint')}
      </p>
    </div>
  );
}

export function OnboardingPage(): JSX.Element {
  const { hasPermission } = useAuth();
  const result = useApi(() => endpoints.onboarding(), []);
  const [skipReason, setSkipReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<unknown>(null);
  const { t, locale } = useLanguage();

  if (!hasPermission('ONBOARDING.READ')) {
    return <PermissionDenied reason={t('onboarding.permissionDenied')} />;
  }

  if (result.status === 'loading') return <LoadingState />;
  if (result.status === 'error') return <ErrorBanner error={result.error} onRetry={result.reload} />;

  const readiness = result.data;
  const requiredSteps = readiness.steps.filter((s) => s.required);
  const doneCount = requiredSteps.filter((s) => s.done).length;

  async function skip(): Promise<void> {
    setBusy(true);
    setActionError(null);
    try {
      await endpoints.skipReconciliation(skipReason);
      setSkipReason('');
      result.reload();
    } catch (err) {
      setActionError(err);
    } finally {
      setBusy(false);
    }
  }

  async function activate(): Promise<void> {
    setBusy(true);
    setActionError(null);
    try {
      await endpoints.activateOnboarding();
      result.reload();
    } catch (err) {
      setActionError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="page-header">
        <h1>{t('nav.onboarding')}</h1>
        <StatusBadge value={readiness.status} />
      </div>

      <p className="sub">{t('onboarding.stepsComplete', { done: doneCount, total: requiredSteps.length })}</p>

      {Boolean(actionError) && <ErrorBanner error={actionError} onRetry={() => setActionError(null)} />}

      <div className="card">
        <div className="table-scroll"><table>
          <thead>
            <tr>
              <th>{t('onboarding.colStep')}</th>
              <th>{t('common.status')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {readiness.steps.map((s) => (
              <StepRow key={s.step} step={s} />
            ))}
          </tbody>
        </table></div>
      </div>

      <RequirePermission permission="ONBOARDING.WRITE">
        <SetupForms onChanged={result.reload} />

        {!readiness.reconciliationSkipped && !readiness.steps.find((s) => s.step === 'RECONCILIATION')?.done && (
          <div className="card" style={{ marginTop: 16 }}>
            <h3>{t('onboarding.skipReconciliation')}</h3>
            <p className="sub">{t('onboarding.skipReconciliationHint')}</p>
            <label>
              {t('common.reason')}
              <input value={skipReason} onChange={(e) => setSkipReason(e.target.value)} disabled={busy} style={{ marginLeft: 8, width: 360 }} />
            </label>
            <button type="button" disabled={busy || !skipReason.trim()} onClick={() => void skip()} style={{ marginLeft: 8 }}>
              {t('onboarding.skipWithReason')}
            </button>
          </div>
        )}

        {readiness.reconciliationSkipped && (
          <p className="disabled-note" style={{ marginTop: 12 }}>
            {t('onboarding.reconciliationSkippedNote', {
              at: formatDateTime(readiness.reconciliationSkipped.at, locale),
              reason: readiness.reconciliationSkipped.reason,
            })}
          </p>
        )}

        <div className="card" style={{ marginTop: 16 }}>
          <h3>{t('onboarding.activation')}</h3>
          {readiness.status === 'ACTIVE' ? (
            <p>
              {t('onboarding.activatedOn', {
                at: readiness.activatedAt ? formatDateTime(readiness.activatedAt, locale) : '—',
              })}
            </p>
          ) : (
            <>
              <p className="sub">
                {readiness.blockers.length === 0
                  ? t('onboarding.everyCheckPasses')
                  : t('onboarding.blockersRemain', { n: readiness.blockers.length })}
              </p>
              <button type="button" disabled={busy || readiness.blockers.length > 0} onClick={() => void activate()}>
                {t('onboarding.activateOrganisation')}
              </button>
            </>
          )}
        </div>
      </RequirePermission>
    </div>
  );
}
